import { supabase } from '@/utils/supabase';
import {
  slugify,
  type CourseCreationMode,
  type Language,
} from '@/utils/kursiPlay';
import {
  getKursiPlayDevCreatorId,
  isCreatorForeignKeyError,
  isKursiPlayDevBypassEnabled,
} from '@/utils/kursiPlayDev';
import { runKnowledgeToGamePipeline } from '@/utils/knowledgeToGameEngine';
import { trackKursiPlayEvent } from '@/utils/kursiPlayTelemetry';

type CreateAiCourseInput = {
  creatorId?: string | null;
  name: string;
  description?: string;
  language: Language;
  difficulty: number;
  creationMode: CourseCreationMode;
  sourceText?: string;
  targetAudience?: string;
};

export type CourseStructureHealth = {
  unitsCount: number;
  lessonsCount: number;
  exercisesCount: number;
  lessonsWithoutExercises: number;
  isReadyToPublish: boolean;
  errors: string[];
};

const GENERATE_FUNCTION_NAME = 'kursi-play-generate';
const TESTING_MODE_ENABLED = true;
const GENERATION_TIMEOUT_MS = 120_000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => {
      reject(
        new Error('תם הזמן ליצירת הקורס. נסה שוב בעוד רגע.'),
      );
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  });
}

function isSlugConflict(message?: string | null) {
  if (!message) return false;
  const normalized = message.toLowerCase();
  return (
    normalized.includes('duplicate key') ||
    normalized.includes('unique') ||
    normalized.includes('gamified_courses_slug_key')
  );
}

async function insertCourseDraft(input: CreateAiCourseInput, slug: string) {
  const payload = {
    name: input.name,
    slug,
    description: input.description || null,
    language: input.language,
    difficulty: input.difficulty,
    creation_mode: input.creationMode,
    status: 'generating',
    target_audience: input.targetAudience || null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    ...(input.creatorId ? { creator_id: input.creatorId } : {}),
  };

  return supabase
    .from('gamified_courses')
    .insert(payload)
    .select('id,slug')
    .single();
}

export async function createAiCourseDraft(
  input: CreateAiCourseInput,
): Promise<{ courseId: string; slug: string }> {
  const pipeline = runKnowledgeToGamePipeline(
    input.sourceText || input.description || input.name,
  );

  const baseSlug = slugify(input.name);

  let createResult:
    | Awaited<ReturnType<typeof insertCourseDraft>>
    | null = null;
  let slug = '';

  for (let attempt = 0; attempt < 3; attempt += 1) {
    slug = `${baseSlug}-${Math.random().toString(36).slice(2, 8)}`;
    createResult = await insertCourseDraft(input, slug);
    if (!createResult.error || !isSlugConflict(createResult.error.message)) {
      break;
    }
  }

  if (!createResult) {
    throw new Error('לא ניתן ליצור טיוטת קורס');
  }

  if (
    (createResult.error || !createResult.data) &&
    isKursiPlayDevBypassEnabled() &&
    isCreatorForeignKeyError(createResult.error?.message)
  ) {
    const fallbackCreatorId = getKursiPlayDevCreatorId();
    createResult = await insertCourseDraft(
      {
        ...input,
        creatorId: fallbackCreatorId,
      },
      slug,
    );
  }

  const course = createResult.data;
  const createErr = createResult.error;

  if (createErr || !course) {
    throw new Error(createErr?.message || 'לא ניתן ליצור טיוטת קורס');
  }

  const { data: generated, error: genErr } = await withTimeout(
    supabase.functions.invoke(GENERATE_FUNCTION_NAME, {
      body: {
        course_id: course.id,
        creator_id: input.creatorId || null,
        course_name: input.name,
        content_text:
          input.sourceText ||
          input.description ||
          input.name ||
          'קורס בדיקות זמני',
        knowledge_chunks: pipeline.chunks,
        playable_units: pipeline.playableUnits,
        generation_mode: 'knowledge_to_game',
        language: input.language,
        difficulty: input.difficulty,
        creation_mode: input.creationMode,
        target_audience: input.targetAudience,
        testing_mode: TESTING_MODE_ENABLED,
      },
    }),
    GENERATION_TIMEOUT_MS,
  );

  if (genErr || !generated?.success) {
    await supabase
      .from('gamified_courses')
      .update({ status: 'draft' })
      .eq('id', course.id);

    if (!TESTING_MODE_ENABLED) {
      throw new Error(
        generated?.error || genErr?.message || 'AI generation נכשל',
      );
    }

    console.warn('[kursi-play] generation failed in permissive mode, keeping draft', {
      courseId: course.id,
      error: generated?.error || genErr?.message || 'unknown_error',
    });

    return { courseId: course.id, slug: course.slug };
  }

  await supabase
    .from('gamified_courses')
    .update({ status: 'draft' })
    .eq('id', course.id);

  const health = await getCourseStructureHealth(course.id);
  if (health.unitsCount < 1 || health.lessonsCount < 1 || health.exercisesCount < 1) {
    throw new Error(
      'הקורס נוצר חלקית בלבד. נסה להפיק מחדש או עדכן את חומר המקור.',
    );
  }

  return { courseId: course.id, slug: course.slug };
}

export async function getCreatorCourseSnapshot(courseId: string): Promise<{
  id: string;
  slug: string;
  status: 'draft' | 'generating' | 'published' | 'archived';
} | null> {
  const { data, error } = await supabase
    .from('gamified_courses')
    .select('id,slug,status')
    .eq('id', courseId)
    .maybeSingle();

  if (error || !data) return null;

  return data as {
    id: string;
    slug: string;
    status: 'draft' | 'generating' | 'published' | 'archived';
  };
}

export async function publishAiCourse(worldId: string): Promise<void> {
  trackKursiPlayEvent('creator_publish_clicked', { world_id: worldId });

  const { data: world, error: worldReadError } = await supabase
    .from('kp_worlds')
    .select('id')
    .eq('id', worldId)
    .maybeSingle();

  if (worldReadError) {
    trackKursiPlayEvent('creator_publish_failed', { world_id: worldId, message: worldReadError.message });
    throw new Error(worldReadError.message);
  }

  if (!world) {
    const message = 'המסלול לא נמצא במודל הקנוני של KURSI LEARN (kp_worlds).';
    trackKursiPlayEvent('creator_publish_failed', { world_id: worldId, message });
    throw new Error(message);
  }

  const { error } = await supabase
    .from('kp_worlds')
    .update({
      status: 'published',
      published_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', worldId);

  if (error) {
    trackKursiPlayEvent('creator_publish_failed', { world_id: worldId, message: error.message });
    throw new Error(error.message);
  }

  trackKursiPlayEvent('creator_publish_success', { world_id: worldId });
}

export async function getCourseStructureHealth(
  courseId: string,
): Promise<CourseStructureHealth> {
  const { data: units } = await supabase
    .from('gp_units')
    .select('id')
    .eq('course_id', courseId);

  const unitIds = (units ?? []).map((u: any) => u.id);
  const { data: lessons } = unitIds.length
    ? await supabase
        .from('gp_lessons')
        .select('id,unit_id')
        .in('unit_id', unitIds)
    : { data: [] as any[] };

  const lessonIds = (lessons ?? []).map((l: any) => l.id);
  const { data: exercises } = lessonIds.length
    ? await supabase
        .from('gp_exercises')
        .select('lesson_id')
        .in('lesson_id', lessonIds)
    : { data: [] as any[] };

  const lessonExerciseCount = new Map<string, number>();
  for (const item of exercises ?? []) {
    const lessonId = (item as any).lesson_id;
    lessonExerciseCount.set(
      lessonId,
      (lessonExerciseCount.get(lessonId) ?? 0) + 1,
    );
  }

  const lessonsWithoutExercises = (lessons ?? []).filter(
    (lesson: any) => (lessonExerciseCount.get(lesson.id) ?? 0) < 1,
  ).length;

  const health: CourseStructureHealth = {
    unitsCount: units?.length ?? 0,
    lessonsCount: lessons?.length ?? 0,
    exercisesCount: exercises?.length ?? 0,
    lessonsWithoutExercises,
    isReadyToPublish: true,
    errors: [],
  };

  if (health.unitsCount < 1) {
    health.isReadyToPublish = false;
    health.errors.push('לא ניתן לפרסם קורס ללא יחידות לימוד');
  }
  if (health.lessonsCount < 1) {
    health.isReadyToPublish = false;
    health.errors.push('לא ניתן לפרסם קורס ללא שיעורים');
  }
  if (health.exercisesCount < 3) {
    health.isReadyToPublish = false;
    health.errors.push('לא ניתן לפרסם קורס ללא מינימום 3 תרגילים');
  }
  if (health.lessonsWithoutExercises > 0) {
    health.isReadyToPublish = false;
    health.errors.push('לא ניתן לפרסם: יש שיעורים ללא תרגילים');
  }

  return health;
}
